/* ──────────────────────────────────────────────────────────────────────────
 * Mission workflows — §21.
 *
 * The brief's instruction is a scoping decision, not a feature request:
 *
 *   "Freeze unnecessary feature expansion... prove four end-to-end mission
 *    workflows. Each must start from a signal and end with a measurable
 *    operational outcome."
 *
 * So these are not demo scripts that narrate a story. Each mission
 * genuinely executes the platform's engines in sequence, records what each
 * stage produced and how long it took, and ends with a measurement. If an
 * engine returns nothing useful, the mission records that and carries on —
 * a workflow that only completes when everything goes well proves nothing.
 *
 * ── THE HUMAN GAP, STATED UP FRONT ──
 * Every one of these workflows passes through AUTHORIZE, and there is no
 * human here. The mission runner does not skip that stage and does not
 * pretend to perform it. It stops, records `awaitingAuthorisation`, and —
 * only when explicitly asked with `simulateOperator: true` — continues with
 * every operator-dependent timing marked `simulated: true`.
 *
 * That flag propagates into §22. It is the difference between "officer
 * acknowledgement took 45 seconds" and "a simulated officer acknowledged
 * instantly, which measures nothing about real operations". Both appear in
 * the metrics; only one of them is a measurement.
 * ────────────────────────────────────────────────────────────────────────── */

import { step, forecast } from "../geo/services/liveModel";
import { buildTwin } from "../twin/twin";
import { correlateHazards } from "../geo/services/multiHazard";
import { assessServiceStress } from "../geo/services/serviceStress";
import { simulateInterventions } from "../geo/services/interventions";
import { assessMobilityChain } from "../geo/services/mobilityChain";
import { buildResponsePlan } from "../geo/services/emergencyResponse";
import { buildEvidenceEnvelope } from "../spine/evidenceEnvelope";
import { ASSEMBLY_AREA_M2, bandForDensity } from "../geo/services/crowdSafety";
import { time } from "../assurance/assurance";

/* The loop stages from §2, so a mission trace can be read against the
 * architecture it is supposed to demonstrate. */
export const LOOP = [
  "SENSE", "FUSE", "UNDERSTAND", "PREDICT", "SIMULATE",
  "DECIDE", "AUTHORIZE", "ACT", "VERIFY", "LEARN",
];

const advance = (state, minutes) => {
  let s = state;
  for (let i = 0; i < minutes; i += 1) s = step(s);
  return s;
};

const density = (sector) => {
  const area = ASSEMBLY_AREA_M2[sector.id];
  return area ? sector.current / area : null;
};

/* A recorded stage. `ms` is genuinely measured; `simulated` marks anything
 * that stood in for a human or an absent feed. */
function stage(name, loopStage, fn, { simulated = false } = {}) {
  const t = time(name, fn);
  return {
    stage: name,
    loopStage,
    ms: t.ms,
    ok: t.ok,
    error: t.error,
    simulated,
    output: t.result,
  };
}

/* ── Mission 1 — Crowd Surge → Prediction → Diversion → Resolution ────── */
function crowdSurge(liveState, twin, { simulateOperator }) {
  const stages = [];
  let s = liveState;

  /* SENSE — find the sector under most pressure. There is no sensor, so
   * this reads the model and says so. */
  const sensed = stage("sense_crowd", "SENSE", () => {
    const worst = [...s.sectors]
      .map((x) => ({ ...x, d: density(x) }))
      .filter((x) => x.d != null)
      .sort((a, b) => b.d - a.d)[0];
    return { sector: worst.id, density: Number(worst.d.toFixed(2)), band: bandForDensity(worst.d).id };
  }, { simulated: true });
  stages.push(sensed);
  const target = sensed.output.sector;

  stages.push(stage("fuse_context", "FUSE", () => ({
    hazards: correlateHazards(s, { twin, serviceStress: assessServiceStress(s) })
      .sectors.find((x) => x.id === target),
  })));

  stages.push(stage("understand", "UNDERSTAND", () => {
    const sec = s.sectors.find((x) => x.id === target);
    return {
      occupancyPct: Math.round((sec.current / sec.capacity) * 100),
      ingressEgressRatio: sec.ingressEgressRatio,
      exitsMapped: sec.exitGates,
    };
  }));

  const predicted = stage("predict", "PREDICT", () => {
    const fc = forecast(s, [15, 30, 60]);
    return Object.fromEntries([15, 30, 60].map((h) => [h, fc[h].find((x) => x.id === target)]));
  });
  stages.push(predicted);

  /* PREDICT → lead time. The gap between now and the projected band
   * change is the number §22 reports as prediction lead time. */
  const nowBand = sensed.output.band;
  const leadMinutes = [15, 30, 60].find(
    (h) => predicted.output[h] && predicted.output[h].band > nowBand,
  ) ?? null;

  const simulated = stage("simulate", "SIMULATE", () =>
    simulateInterventions(s, target, { twin }));
  stages.push(simulated);

  const decided = stage("decide", "DECIDE", () => ({
    recommended: simulated.output.recommended?.id ?? null,
    noOptionHelps: simulated.output.noOptionHelps,
    alternativesConsidered: simulated.output.options.length,
  }));
  stages.push(decided);

  const chosen = simulated.output.recommended ?? null;

  if (!simulateOperator) {
    return finish("crowd_surge", stages, {
      awaitingAuthorisation: true,
      target, leadMinutes,
      note: "Mission halted at AUTHORIZE. No human is present, and the runner does not perform that stage on an officer's behalf.",
    });
  }

  stages.push(stage("authorize", "AUTHORIZE", () => ({
    approvedOption: chosen?.id ?? null,
    by: "SIMULATED-OFFICER",
  }), { simulated: true }));

  /* ACT — apply the chosen option's levers and run the model on. This is
   * the part that makes the outcome measurable rather than asserted. */
  const before = density(s.sectors.find((x) => x.id === target));
  const acted = stage("act", "ACT", () => {
    if (!chosen) return { applied: false, reason: "No option was recommended." };
    const opts = {
      inflowMultipliers: chosen.key === "regulate_gate" ? { [target]: 0.55 }
        : chosen.key === "public_advisory" ? { [target]: 0.82 }
        : chosen.key === "change_barricade" ? { [target]: 0.8 } : null,
      egressMultipliers: ["open_corridor", "deploy_police", "change_barricade"].includes(chosen.key)
        ? { [target]: 1.3 } : null,
    };
    s = advance({ ...s, sectors: s.sectors.map((x) => ({ ...x })) }, 30);
    let withOpt = liveState;
    for (let i = 0; i < 30; i += 1) withOpt = step(withOpt, opts);
    return { applied: true, option: chosen.id, counterfactual: withOpt };
  }, { simulated: true });
  stages.push(acted);

  const verified = stage("verify", "VERIFY", () => {
    if (!acted.output.applied) return { verified: false, reason: acted.output.reason };
    const after = density(s.sectors.find((x) => x.id === target));
    const cf = acted.output.counterfactual.sectors.find((x) => x.id === target);
    const withOption = density(cf);
    return {
      verified: true,
      densityBefore: Number(before.toFixed(2)),
      densityDoNothing: Number(after.toFixed(2)),
      densityWithOption: Number(withOption.toFixed(2)),
      improvement: Number((after - withOption).toFixed(2)),
      /* The caveat that keeps this from being a claim of causation. */
      caveat: "Compared against the model's own do-nothing run, not against reality. It shows the option's modelled effect, not its real effect.",
    };
  });
  stages.push(verified);

  stages.push(stage("learn", "LEARN", () => ({
    recorded: true,
    note: "Outcome retained for the evidence envelope. No model weights are updated — there is nothing to learn from a simulation of itself.",
  })));

  return finish("crowd_surge", stages, {
    target, leadMinutes,
    outcome: verified.output,
    interventionEffective: verified.output.improvement > 0.05,
  });
}

/* ── Mission 2 — Medical Emergency → Resource → Dispatch → Resolution ─── */
function medicalEmergency(liveState, twin, { simulateOperator }) {
  const stages = [];
  const incident = {
    id: "MSN-MED-01", caseType: "cardiac", sector: "SEC-01",
    coord: [73.7900, 19.9985], detectedAt: Date.now(),
  };

  stages.push(stage("sense_report", "SENSE", () => ({
    source: "field_officer_report",
    incident: incident.id,
    note: "No sensor detected this. A field report is the only detection path the platform has.",
  }), { simulated: true }));

  stages.push(stage("understand_case", "UNDERSTAND", () => ({
    caseType: incident.caseType,
    sectorDensity: Number((density(liveState.sectors.find((x) => x.id === incident.sector)) ?? 0).toFixed(2)),
  })));

  const plan = stage("select_resource", "DECIDE", () =>
    buildResponsePlan(incident, liveState, { twin }));
  stages.push(plan);

  if (!simulateOperator) {
    return finish("medical_emergency", stages, {
      awaitingAuthorisation: true,
      note: "Plan prepared. Dispatch requires an authorised officer; the runner will not perform that stage.",
      plan: plan.output,
    });
  }

  stages.push(stage("authorize_dispatch", "AUTHORIZE", () => ({
    approved: plan.output.primaryResponder?.id ?? null,
    by: "SIMULATED-OFFICER",
  }), { simulated: true }));

  stages.push(stage("dispatch", "ACT", () => ({
    unit: plan.output.primaryResponder?.id ?? null,
    etaMinutes: plan.output.estimatedResponseMinutes,
    receiving: plan.output.receivingFacility?.id ?? null,
  }), { simulated: true }));

  const verified = stage("verify_response", "VERIFY", () => ({
    withinGoldenWindow: plan.output.withinGoldenWindow,
    goldenWindowMinutes: plan.output.goldenWindowMinutes,
    etaMinutes: plan.output.estimatedResponseMinutes,
    warnings: plan.output.warnings,
  }));
  stages.push(verified);

  return finish("medical_emergency", stages, {
    outcome: verified.output,
    /* The measurable end state the brief asks for. */
    responseWithinWindow: plan.output.withinGoldenWindow,
  });
}

/* ── Mission 3 — Transport Surge → Crowd Impact → Intervention ────────── */
function transportSurge(liveState, twin, { simulateOperator }) {
  const stages = [];

  const sensed = stage("sense_transport", "SENSE", () =>
    assessMobilityChain(liveState, { twin }), { simulated: true });
  stages.push(sensed);

  /* The §11 payload is the whole point of this mission: the transport
   * signal must become a crowd prediction, not stay a transport alert. */
  const impact = stage("crowd_impact", "UNDERSTAND", () => {
    const chain = sensed.output;
    const toGhat = chain.propagation.filter((p) => p.toStage === "ghat");
    return {
      binding: chain.binding?.stage ?? null,
      bindingAgency: chain.binding?.agency ?? null,
      reachesGhatIn: toGhat[0]?.inMinutes ?? null,
      peopleAffected: toGhat[0]?.cumulativePeople ?? null,
      translated: toGhat.length > 0,
    };
  });
  stages.push(impact);

  const held = stage("simulate_hold", "SIMULATE", () =>
    assessMobilityChain(liveState, { disruptions: { bus: 0 }, twin }));
  stages.push(held);

  const decided = stage("decide_hold", "DECIDE", () => {
    const base = sensed.output.chainThroughputPerMin;
    const withHold = held.output.chainThroughputPerMin;
    return {
      baselineThroughput: base,
      withHoldThroughput: withHold,
      reduction: base - withHold,
      /* A hold is only correct if the ghat is the constraint. Holding
       * transport into an emptying ghat strands people for nothing. */
      holdJustified: impact.output.binding === "ghat" || impact.output.binding === "corridor",
    };
  });
  stages.push(decided);

  if (!simulateOperator) {
    return finish("transport_surge", stages, {
      awaitingAuthorisation: true,
      note: "A transport hold strands passengers upstream and requires transport-department authorisation.",
    });
  }

  stages.push(stage("authorize_hold", "AUTHORIZE", () => ({
    approved: decided.output.holdJustified,
    by: "SIMULATED-OFFICER",
  }), { simulated: true }));

  const verified = stage("verify_stabilisation", "VERIFY", () => ({
    chainThroughputPerMin: held.output.chainThroughputPerMin,
    deferredSurge: held.output.propagation.filter((p) => p.effect === "shortfall").length,
    caveat: "Held passengers do not disappear. The deferred cohort arrives later as a sharper peak, which this verification does not yet measure.",
  }));
  stages.push(verified);

  return finish("transport_surge", stages, {
    outcome: verified.output,
    crowdImpactTranslated: impact.output.translated,
  });
}

/* ── Mission 4 — Multi-Hazard → Multi-Agency → Evidence ───────────────── */
function multiHazard(liveState, twin, { simulateOperator }) {
  const stages = [];

  const correlated = stage("correlate", "UNDERSTAND", () =>
    correlateHazards(liveState, { twin, serviceStress: assessServiceStress(liveState) }));
  stages.push(correlated);

  const worst = correlated.output.sectors[0];

  const agencies = stage("assign_agencies", "DECIDE", () => {
    /* Which departments the live hazards actually implicate — derived
     * from the hazards present, not from a fixed list. */
    const map = {
      density: "POLICE", egress: "POLICE", routeBlockage: "POLICE",
      heat: "HEALTH", medical: "HEALTH", disease: "HEALTH",
      water: "NMC", utility: "NMC", rainfall: "NMC",
      traffic: "TRANSPORT", flood: "REVENUE", comms: "COMMS",
      fire: "FIRE", structural: "NMC", security: "POLICE",
      missingPersons: "POLICE",
    };
    const live_ = worst.hazards.filter((h) => h.severity != null && h.severity > 0.3);
    const involved = [...new Set(live_.map((h) => map[h.key]).filter(Boolean))];
    return {
      sector: worst.id,
      level: worst.level,
      hazardsInPlay: live_.map((h) => h.key),
      agencies: involved,
      /* The multi-agency point: more than one department has to act, and
       * the platform says which, derived from what is actually elevated. */
      isMultiAgency: involved.length > 1,
    };
  });
  stages.push(agencies);

  const incident = {
    id: "MSN-MH-01", sector: worst.id, coord: worst.centre,
    detectedAt: Date.now(),
    assignments: agencies.output.agencies.map((a) => ({ agency: a, task: `Respond to ${worst.level} multi-hazard condition` })),
  };

  const events = simulateOperator
    ? [
        { type: "detected", at: 1000 },
        { type: "triaged", at: 1200, actor: "SIMULATED-OFFICER" },
        { type: "authorised", at: 1500, actor: "SIMULATED-OFFICER", decision: "authorised" },
        { type: "dispatched", at: 1600 },
        { type: "acknowledged", at: 1800 },
        { type: "resolved", at: 3600, actor: "SIMULATED-OFFICER" },
      ]
    : [{ type: "detected", at: 1000 }];

  const evidence = stage("evidence", "LEARN", () =>
    buildEvidenceEnvelope({
      incident, events,
      simulation: simulateInterventions(liveState, worst.id, { twin }),
      hazardPicture: correlated.output,
      observations: simulateOperator ? [{ density: 4.1 }, { density: 3.4 }] : [],
    }));
  stages.push(evidence);

  return finish("multi_hazard", stages, {
    awaitingAuthorisation: !simulateOperator,
    sector: worst.id,
    agencies: agencies.output.agencies,
    isMultiAgency: agencies.output.isMultiAgency,
    evidenceCompleteness: evidence.output.completeness.pct,
  });
}

function finish(id, stages, result) {
  const totalMs = Number(stages.reduce((a, s) => a + s.ms, 0).toFixed(2));
  const failed = stages.filter((s) => !s.ok);
  return {
    id,
    stages,
    totalMs,
    /* Measured vs simulated, kept apart. Anything that stood in for a
     * human or an absent feed is excluded from the measured total. */
    measuredMs: Number(
      stages.filter((s) => !s.simulated).reduce((a, s) => a + s.ms, 0).toFixed(2),
    ),
    simulatedStages: stages.filter((s) => s.simulated).map((s) => s.stage),
    loopStagesCovered: [...new Set(stages.map((s) => s.loopStage))],
    failed: failed.map((s) => ({ stage: s.stage, error: s.error })),
    complete: failed.length === 0 && !result.awaitingAuthorisation,
    ...result,
  };
}

export const MISSIONS = {
  crowd_surge: {
    id: "crowd_surge",
    label: { en: "Crowd Surge → Prediction → Diversion → Resolution", mr: "गर्दी वाढ → अंदाज → वळण → निवारण" },
    run: crowdSurge,
  },
  medical_emergency: {
    id: "medical_emergency",
    label: { en: "Medical Emergency → Resource Selection → Dispatch → Resolution", mr: "वैद्यकीय आणीबाणी → संसाधन → रवानगी → निवारण" },
    run: medicalEmergency,
  },
  transport_surge: {
    id: "transport_surge",
    label: { en: "Transport Surge → Crowd Impact → Intervention → Stabilisation", mr: "वाहतूक वाढ → गर्दी परिणाम → हस्तक्षेप → स्थिरीकरण" },
    run: transportSurge,
  },
  multi_hazard: {
    id: "multi_hazard",
    label: { en: "Multi-Hazard Incident → Multi-Agency Coordination → Evidence", mr: "बहु-धोका → बहु-यंत्रणा समन्वय → पुरावा" },
    run: multiHazard,
  },
};

export function runMission(id, liveState, { twin = null, simulateOperator = false } = {}) {
  const m = MISSIONS[id];
  if (!m) return { id, ok: false, reason: `No mission "${id}".` };
  const t = twin ?? buildTwin(liveState);
  return { ok: true, label: m.label, simulateOperator, ...m.run(liveState, t, { simulateOperator }) };
}

export function runAllMissions(liveState, opts = {}) {
  const twin = opts.twin ?? buildTwin(liveState);
  return Object.keys(MISSIONS).map((id) => runMission(id, liveState, { ...opts, twin }));
}

export const MISSION_ASSUMPTIONS = [
  "Each mission genuinely executes the platform's engines in sequence. Stage timings are measured, not narrated.",
  "Every mission halts at AUTHORIZE by default. There is no human here and the runner does not perform that stage on an officer's behalf.",
  "With simulateOperator enabled, operator-dependent stages run and are marked simulated:true. That flag propagates into the §22 metrics so a simulated acknowledgement is never counted as a measurement.",
  "Intervention effectiveness is measured against the model's own do-nothing run, which shows the modelled effect of an option and not its real effect.",
  "Detection is a field report or a model reading in every mission, because the platform has no sensor. Detection latency is therefore not measurable and is reported as such.",
];
